import type { BulkStartingValueLine } from '../types/inventoryStartingValue';
import { BULK_STARTING_VALUE_MAX_LINES } from './inventoryStartingValueForm';

export const BULK_OPENING_STOCK_BATCH_SIZE = BULK_STARTING_VALUE_MAX_LINES;

export type OpeningStockRowStatus = 'valid' | 'invalid' | 'skipped';

export interface OpeningStockImportRow {
  id: string;
  rowNumber: number;
  sku: string;
  qty: string;
  unit_cost: string;
  memo: string;
  status: OpeningStockRowStatus;
  errors: string[];
  removed?: boolean;
}

export interface OpeningStockImportCounts {
  valid: number;
  invalid: number;
  skipped: number;
  total: number;
}

const HEADER_ALIASES: Record<string, 'sku' | 'qty' | 'unit_cost' | 'memo'> = {
  sku: 'sku',
  item_sku: 'sku',
  'item sku': 'sku',
  product_sku: 'sku',
  'product sku': 'sku',
  qty: 'qty',
  quantity: 'qty',
  qty_on_hand: 'qty',
  'qty on hand': 'qty',
  unit_cost: 'unit_cost',
  unitcost: 'unit_cost',
  'unit cost': 'unit_cost',
  cost: 'unit_cost',
  fifo_cost: 'unit_cost',
  'fifo cost': 'unit_cost',
  standard_cost: 'unit_cost',
  memo: 'memo',
  note: 'memo',
  notes: 'memo',
  line_memo: 'memo',
  'line memo': 'memo',
};

const normalizeHeader = (header: string) =>
  header
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const parseNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const normalized = trimmed.replace(/,/g, '');
  const num = Number(normalized);
  return Number.isFinite(num) ? num : null;
};

/** Parse a single CSV record line respecting quoted fields. */
const parseCsvLine = (line: string): string[] => {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const next = line[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      cells.push(current.trim());
      current = '';
      continue;
    }

    current += char;
  }

  cells.push(current.trim());
  return cells;
};

const splitCsvRows = (text: string): string[][] => {
  const normalized = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalized.split('\n').filter((line) => line.trim().length > 0);
  return lines.map(parseCsvLine);
};

const mapHeaders = (headerRow: string[]): Partial<Record<'sku' | 'qty' | 'unit_cost' | 'memo', number>> => {
  const mapping: Partial<Record<'sku' | 'qty' | 'unit_cost' | 'memo', number>> = {};

  headerRow.forEach((header, index) => {
    const key = HEADER_ALIASES[normalizeHeader(header)];
    if (key && mapping[key] === undefined) {
      mapping[key] = index;
    }
  });

  return mapping;
};

const createRowId = (rowNumber: number) => `row-${rowNumber}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export const parseOpeningStockCsv = (text: string): OpeningStockImportRow[] => {
  const table = splitCsvRows(text);
  if (table.length === 0) return [];

  const headerMapping = mapHeaders(table[0]);
  const hasHeader = headerMapping.sku !== undefined || headerMapping.qty !== undefined;
  const dataRows = hasHeader ? table.slice(1) : table;

  const skuIndex = headerMapping.sku ?? (hasHeader ? -1 : 0);
  const qtyIndex = headerMapping.qty ?? (hasHeader ? -1 : 1);
  const unitCostIndex = headerMapping.unit_cost ?? (hasHeader ? -1 : 2);
  const memoIndex = headerMapping.memo ?? (hasHeader ? -1 : 3);

  return dataRows.map((cells, index) => {
    const rowNumber = hasHeader ? index + 2 : index + 1;
    return {
      id: createRowId(rowNumber),
      rowNumber,
      sku: skuIndex >= 0 ? (cells[skuIndex] ?? '').trim() : '',
      qty: qtyIndex >= 0 ? (cells[qtyIndex] ?? '').trim() : '',
      unit_cost: unitCostIndex >= 0 ? (cells[unitCostIndex] ?? '').trim() : '',
      memo: memoIndex >= 0 ? (cells[memoIndex] ?? '').trim() : '',
      status: 'invalid' as OpeningStockRowStatus,
      errors: [] as string[],
    };
  });
};

export const validateOpeningStockRows = (rows: OpeningStockImportRow[]): OpeningStockImportRow[] => {
  const activeRows = rows.filter((row) => !row.removed);
  const skuCounts = new Map<string, number>();

  activeRows.forEach((row) => {
    const skuKey = row.sku.trim().toLowerCase();
    if (skuKey) {
      skuCounts.set(skuKey, (skuCounts.get(skuKey) ?? 0) + 1);
    }
  });

  return rows.map((row) => {
    if (row.removed) {
      return { ...row, status: 'skipped', errors: [] };
    }

    const errors: string[] = [];
    const sku = row.sku.trim();
    const qtyNum = parseNumber(row.qty);
    const unitCostNum = parseNumber(row.unit_cost);

    if (!sku) {
      errors.push('SKU is required');
    } else if ((skuCounts.get(sku.toLowerCase()) ?? 0) > 1) {
      errors.push('Duplicate SKU in file');
    }

    if (qtyNum === null || row.qty.trim() === '') {
      errors.push('Quantity is required');
    } else if (qtyNum === 0) {
      return { ...row, status: 'skipped', errors: ['Quantity is zero — skipped'] };
    } else if (qtyNum < 0) {
      errors.push('Quantity must be greater than zero');
    }

    if (unitCostNum === null || row.unit_cost.trim() === '') {
      errors.push('Unit cost is required');
    } else if (unitCostNum < 0) {
      errors.push('Unit cost cannot be negative');
    }

    if (errors.length > 0) {
      return { ...row, status: 'invalid', errors };
    }

    return { ...row, status: 'valid', errors: [] };
  });
};

export const countOpeningStockRows = (rows: OpeningStockImportRow[]): OpeningStockImportCounts => {
  const active = rows.filter((row) => !row.removed);
  return {
    valid: active.filter((row) => row.status === 'valid').length,
    invalid: active.filter((row) => row.status === 'invalid').length,
    skipped: active.filter((row) => row.status === 'skipped').length,
    total: active.length,
  };
};

export const lineTotalForRow = (row: OpeningStockImportRow): number => {
  const qty = parseNumber(row.qty) ?? 0;
  const unitCost = parseNumber(row.unit_cost) ?? 0;
  return qty * unitCost;
};

export const totalValueForRows = (rows: OpeningStockImportRow[]): number =>
  rows
    .filter((row) => !row.removed && row.status === 'valid')
    .reduce((sum, row) => sum + lineTotalForRow(row), 0);

export const chunkOpeningStockRows = (
  rows: OpeningStockImportRow[],
  size = BULK_OPENING_STOCK_BATCH_SIZE
): OpeningStockImportRow[][] => {
  const valid = rows.filter((row) => !row.removed && row.status === 'valid');
  const chunks: OpeningStockImportRow[][] = [];
  for (let i = 0; i < valid.length; i += size) {
    chunks.push(valid.slice(i, i + size));
  }
  return chunks;
};

export const rowsToApiLines = (rows: OpeningStockImportRow[]): BulkStartingValueLine[] =>
  rows.map((row) => {
    const line: BulkStartingValueLine = {
      sku: row.sku.trim(),
      qty: parseNumber(row.qty) ?? 0,
      unit_cost: parseNumber(row.unit_cost) ?? 0,
    };
    if (row.memo.trim()) {
      line.memo = row.memo.trim();
    }
    return line;
  });

export const exportRowsToCsv = (
  rows: OpeningStockImportRow[],
  filename: string,
  includeErrors = true
): void => {
  const headers = includeErrors
    ? ['sku', 'qty', 'unit_cost', 'memo', 'status', 'errors']
    : ['sku', 'qty', 'unit_cost', 'memo'];

  const escape = (value: string) => `"${value.replace(/"/g, '""')}"`;

  const body = rows.map((row) => {
    const cells = [
      row.sku,
      row.qty,
      row.unit_cost,
      row.memo,
      ...(includeErrors ? [row.status, row.errors.join('; ')] : []),
    ];
    return cells.map(escape).join(',');
  });

  const csv = [headers.join(','), ...body].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export const OPENING_STOCK_CSV_TEMPLATE = `sku,qty,unit_cost,memo
WDG-001,150,42.50,Optional line memo
WDG-002,80,12.00,
`;
